import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api from '../../../../api.js';
import clientLogger from '@src/utils/client-logger';
import { getCompanyIdFromRPId, getCompanyProfile } from '@src/config/server-config.js';
import type { NonExecutivesFeePolicyResponse } from '@shared/types/non-executives-fee-policy';
import { GetCompanyIdFromRPIdResponse } from '@shared/types/index.js';

export interface PayForPerformanceState {
  data: any | null;
  loading: boolean;
  error: string | null;
  cgiCompanyId: number | null;
}

const initialState: PayForPerformanceState = {
  data: null,
  loading: false,
  error: null,
  cgiCompanyId: null,
};

export const GetPayForPerformanceData = async (pid: string) => {
  const profileResponse = await api.post(getCompanyProfile, { pid: +pid });
  const rpCompanyId = profileResponse.data?.RPCompany_ID;

  if (!rpCompanyId) {
    throw new Error('RPCompanyID not found for this company');
  }

  const companyIdResponse = await api.get<GetCompanyIdFromRPIdResponse>(getCompanyIdFromRPId, {
    params: { rp_ids: !Array.isArray(rpCompanyId) ? [rpCompanyId] : rpCompanyId },
  });

  const mapping = companyIdResponse.data?.data;
  const mappedRaw = typeof mapping === 'object' && mapping !== null ? mapping[rpCompanyId] : undefined;
  const mappedNumeric = mappedRaw != null && Number.isFinite(Number(mappedRaw)) ? Number(mappedRaw) : undefined;

  if (mappedNumeric == null) {
    throw new Error('Company ID mapping not found');
  }

  let primaryCgiCompanyId = mappedNumeric;
  let fallbackCgiCompanyId: number | null = null;

  try {
    const feeRes = await api.get<NonExecutivesFeePolicyResponse>(
      `/api/v2/non-executives-fee-policy/${encodeURIComponent(pid)}`,
    );
    const entity = feeRes.data?.data?.entity;
    if (entity && typeof entity.id === 'number' && Number.isFinite(entity.id)) {
      const rpIdFromEntity = entity.rp_id;
      const sameRp =
        rpIdFromEntity != null &&
        String(rpIdFromEntity).trim() !== '' &&
        String(rpIdFromEntity) === String(rpCompanyId);
      if (sameRp && entity.id !== mappedNumeric) {
        primaryCgiCompanyId = entity.id;
        fallbackCgiCompanyId = mappedNumeric;
      }
    }
  } catch (feePolicyError) {
    clientLogger.warn('[PayForPerformance] non-executives-fee-policy lookup failed; using mapped CGI company id', {
      pid,
      mappedCgiCompanyId: mappedNumeric,
      status: (feePolicyError as { response?: { status?: number } })?.response?.status,
    });
  }

  const fetchP4pInfo = (cgiCompanyId: number) => api.get(`/api/v2/CompanyP4PRoute/${cgiCompanyId}/info`);

  try {
    const response = await fetchP4pInfo(primaryCgiCompanyId);
    return { data: response.data, companyId: primaryCgiCompanyId };
  } catch (firstErr) {
    if (fallbackCgiCompanyId != null) {
      const response = await fetchP4pInfo(fallbackCgiCompanyId);
      return { data: response.data, companyId: fallbackCgiCompanyId };
    }
    throw firstErr;
  }
};

export const fetchPayForPerformance = createAsyncThunk(
  'payForPerformance/fetch',
  async ({ companyId }: { companyId: string }) => {
    return GetPayForPerformanceData(companyId);
  },
);

const PayForPerformanceSlice = createSlice({
  name: 'payForPerformance',
  initialState,
  reducers: {
    resetPayForPerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayForPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayForPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload?.data ?? null;
        state.cgiCompanyId = action.payload?.companyId ?? null;
        state.error = null;
      })
      .addCase(fetchPayForPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to load pay for performance data';
      });
  },
});

export const { resetPayForPerformance } = PayForPerformanceSlice.actions;

export default PayForPerformanceSlice.reducer;
